import {createContext, useContext, type ComponentProps, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import type {AccentColor} from '../lib/color';
import {Typography} from '../Typography';

export type CardProps = ComponentProps<'div'> & {active?: AccentColor | false};

export const Card = ({
	active, className, children, ...props
}: CardProps) => (
	<CardContext.Provider value={true}>
		<div
			data-slot='card'
			className={cn(
				'relative flex w-full shrink-0 flex-col items-start gap-6 break-words ' +
				'rounded-lg bg-background-800 p-6 shadow-l',
				'border-l-4',
				active ? activeStyles[active] : 'border-l-transparent',
				className
			)}
			{...props}>
			{children}
		</div>
	</CardContext.Provider>
);

Card.displayName = 'Card';

export interface CardHeaderProps {
	title?: string;
	action?: ReactNode;
}

export const CardHeader = ({title, action}: CardHeaderProps) => {
	useInCard('CardHeader');

	if (!title && !action) return null;

	return (
		<>
			{title && <Typography data-slot='card-title' variant='body2' color='textSecondary'>{title}</Typography>}
			{action && <div data-slot='card-action' className='absolute right-6 top-6'>{action}</div>}
		</>
	);
};

CardHeader.displayName = 'CardHeader';

export const CardContent = ({className, children, ...props}: ComponentProps<'div'>) => {
	useInCard('CardContent');

	return (
		<div data-slot='card-content' className={cn('w-full', className)} {...props}>
			{typeof children === 'string' ? <Typography variant='body3'>{children}</Typography> : children}
		</div>
	);
};

CardContent.displayName = 'CardContent';

export type CardFooterProps = ComponentProps<'div'> & {divider?: boolean};

export const CardFooter = ({
	divider = false, className, children, ...props
}: CardFooterProps) => {
	useInCard('CardFooter');

	return (
		<div
			data-slot='card-footer'
			className={cn('mt-auto flex w-full items-center', divider && 'border-t border-foreground-400 pt-6', className)}
			{...props}>
			{children}
		</div>
	);
};

CardFooter.displayName = 'CardFooter';

const CardContext = createContext(false);

function useInCard(part: string) {
	const inCard = useContext(CardContext);
	if (!inCard) throw new Error(`${part} must be rendered inside Card.`);
}

const activeStyles = {
	primary: 'border-l-brand-main-light',
	critical: 'border-l-tone-red',
	medium: 'border-l-tone-orange',
	low: 'border-l-tone-yellow',
	success: 'border-l-tone-green',
	info: 'border-l-tone-light-blue'
} satisfies Record<AccentColor, string>;
